import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '@/i18n';

/**
 * Top-level error boundary. Without one, ANY render-time throw unmounts the whole
 * React tree and the user is left staring at the near-black `--background` body —
 * the "screen stays black/blank" class of bug reports. Deliberately renders without
 * React context (no hooks, no router): it must work even when everything else is
 * broken. Copy comes from the i18n SINGLETON (v2 namespace, localized in every
 * locale JSON) with hardcoded English fallbacks in case i18n itself is the crash.
 */
type Props = { children: ReactNode };
type State = { hasError: boolean };

const msg = (key: string, fallback: string): string => {
  try {
    return i18n.t(`v2:errorBoundary.${key}`, { defaultValue: fallback });
  } catch {
    return fallback;
  }
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 16,
          background: '#1A1A24', color: '#fff', textAlign: 'center', padding: 24,
        }}
      >
        <div style={{ fontSize: 40 }}>😵</div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          {msg('title', 'Something went wrong')}
        </div>
        <div style={{ fontSize: 14, opacity: 0.7, maxWidth: 360 }}>
          {msg('description', 'An unexpected error occurred. Reloading usually fixes it — your projects and videos are safe.')}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 8, padding: '10px 24px', borderRadius: 12, border: 'none',
            background: '#6C5CE7', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {msg('reload', 'Reload')}
        </button>
      </div>
    );
  }
}
